import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from './firebase';
import {
  doc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp
} from 'firebase/firestore';

/**
 * 🔄 TableRedirect - Componente per QR Code Permanenti (HashRouter)
 * 
 * Funzionamento:
 * 1. Cliente scansiona QR stampato: /#/table-redirect?table=A1&locale=ciauru
 * 2. Questo componente legge il token ATTUALE da Firebase
 * 3. Redirect automatico a /menu con token attuale usando HashRouter
 * 4. MenuView funziona normalmente
 * 
 * Risultato: QR stampato funziona per sempre, anche se token cambia!
 */
export default function TableRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const redirectToMenu = async () => {

      try {
        let params;

        // 👉 PRIORITÀ: URL normale (?table=...)
        if (location.search && location.search.includes("table")) {
          params = new URLSearchParams(location.search);
        } else {
          // 👉 fallback HashRouter
          const hash = window.location.hash;
          const queryString = hash.includes("?") ? hash.split("?")[1] : "";
          params = new URLSearchParams(queryString);
        }

        const tableId = params.get('table');
        const locale = params.get('locale');

        console.log(`🔍 URL completo:`, window.location.href);
        console.log(`🔍 Hash:`, window.location.hash);
        console.log(`🔍 Location.search:`, location.search);
        console.log(`📋 Parametri trovati - table: ${tableId}, locale: ${locale}`);

        // Validazione parametri
        if (!tableId) {
          console.error('❌ Parametro table mancante');
          setError('QR Code non valido: manca ID tavolo');
          setIsLoading(false);
          return;
        }

        if (!locale) {
          console.error('❌ Parametro locale mancante');
          setError('QR Code non valido: manca locale');
          setIsLoading(false);
          return;
        }

        console.log(`🔄 TableRedirect attivato!`);
        console.log(`📍 Tavolo: ${tableId}`);
        console.log(`🏪 Locale: ${locale}`);

        // 🔥 CONTROLLO STATO LOCALE
        const localeRef = doc(db, 'locali', locale);
        const localeSnap = await getDoc(localeRef);
        if (!localeSnap.exists()) {
          console.error("❌ Locale non trovato");
          setError("Locale non valido");
          setIsLoading(false);
          return;
        }

        const localeData = localeSnap.data();

        // 🔥 SOLO se esplicitamente bloccato
        if (localeData.stato && localeData.stato === "bloccato") {
          console.log("🚫 Locale bloccato");
          setError("LOCALE_BLOCCATO");
          setIsLoading(false);
          return;
        }

        // 🔥 DEBUG FONDAMENTALE
        console.log("✅ Locale ATTIVO, procedo al redirect");
        // 2. Leggi token ATTUALE da Firebase
        const tavoloRef = doc(db, 'locali', locale, 'tavoli', tableId);
        console.log(`🔍 Cerco token in Firebase: locali/${locale}/tavoli/${tableId}`);
        
        const tavoloSnap = await getDoc(tavoloRef);

        if (!tavoloSnap.exists()) {
          console.error(`❌ Tavolo ${tableId} non trovato in Firebase`);
          setError(`Tavolo ${tableId} non configurato. Contatta il personale.`);
          setIsLoading(false);
          return;
        }

        const tavoloData = tavoloSnap.data();
        console.log(`📦 Dati tavolo:`, tavoloData);

        const tokenAttuale = tavoloData.tokenAttuale;

        if (!tokenAttuale) {
          console.error('❌ Tavolo esiste ma senza token');
          setError('Tavolo non configurato correttamente. Contatta il personale.');
          setIsLoading(false);
          return;
        }

        // 📊 TRACKING ACCESSO QR REALE CLIENTE
try {

  let clienteId =
    localStorage.getItem("clienteId");

  if (!clienteId) {

    clienteId =
      crypto.randomUUID();

    localStorage.setItem(
      "clienteId",
      clienteId
    );
  }


  const trackingPromise = addDoc(
    collection(db, "analytics_menuview"),
    {
      localeTag: locale,
      tableId: String(tableId),
      token: tokenAttuale,

      clienteId,

      timestamp: serverTimestamp(),

      source: "qr_real",
      type: "MenuView",

      platform: navigator.platform || null,
      language: navigator.language || null,
      userAgent: navigator.userAgent || null
    }
  );

  await trackingPromise;

  console.log("📊 Accesso QR reale registrato");

  // aspetta realmente il flush Firestore
  await new Promise(resolve => setTimeout(resolve, 700));

} catch (trackErr) {

  console.error(
    "❌ Errore tracking QR:",
    trackErr
  );

}

        // 3. Redirect a MenuView con token attuale
        console.log(`✅ Token attuale trovato: ${tokenAttuale}`);
        const menuUrl = `/menu/${locale}/${tableId}?token=${tokenAttuale}`;
        console.log(`🔄 Redirect a: ${menuUrl}`);
        
        // Usa navigate per HashRouter
        console.log("🚀 NAVIGATE PARTITO");
        navigate(menuUrl, { replace: true });

      } catch (err) {
        console.error('❌ Errore durante redirect:', err);
        console.error('Stack trace:', err.stack);
        setError('Errore di connessione. Verifica la connessione internet e riprova.');
        setIsLoading(false);
      }
    };

    redirectToMenu();
  }, [navigate, location]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '20px',
        textAlign: 'center'
      }}>
        {/* Spinner animato */}
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite',
          marginBottom: '24px'
        }}></div>
        
        <p style={{
          fontSize: '18px',
          color: '#666',
          margin: '0 0 8px 0'
        }}>
          Caricamento menu...
        </p>
        
        <p style={{
          fontSize: '14px',
          color: '#999',
          margin: 0
        }}>
          Attendere prego
        </p>

        {/* CSS Animation */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#fff',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '32px',
        textAlign: 'center'
      }}>
        {/* Icona errore */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#ffebee',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px'
        }}>
          <span style={{ fontSize: '40px' }}>{error === "LOCALE_BLOCCATO" ? "🚫" : "⚠️"}</span>
        </div>

        {/* Messaggio errore */}
        <h2>
          {error === "LOCALE_BLOCCATO"
            ? "Locale disattivato"
            : "QR Code non valido"}
        </h2>

        <p>
          {error === "LOCALE_BLOCCATO"
            ? "Contatta l'amministratore"
            : error}
        </p>

        {/* Debug info */}
        <details style={{
          fontSize: '12px',
          color: '#999',
          marginBottom: '24px',
          maxWidth: '400px',
          textAlign: 'left'
        }}>
          <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>
            Informazioni tecniche (per sviluppatori)
          </summary>
          <pre style={{
            backgroundColor: '#f5f5f5',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '11px'
          }}>
            URL: {window.location.href}{'\n'}
            Hash: {window.location.hash}{'\n'}
            Search: {location.search}
          </pre>
        </details>

        {/* Azioni */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: '100%',
          maxWidth: '300px'
        }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5568d3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#667eea'}
          >
            🔄 Riprova
          </button>

          <p style={{
            fontSize: '14px',
            color: '#999',
            margin: '16px 0 0 0'
          }}>
            Oppure contatta un membro del nostro staff
          </p>
        </div>
      </div>
    );
  }

  // Non dovrebbe mai arrivare qui (redirect immediato)
  return null;
}
